import { NumberType_Enum } from '@weave/schema-gen-ts/dist/schemas/comm/phone-number/v1/phone_number_service.pb';
import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { BulkMessagesQueries } from '@frontend/api-messaging';
import { BulkTextTemplateEditor, DEFAULT_BULK_SHORT_URL } from '@frontend/bulk-text-template-editor';
import { useTranslation } from '@frontend/i18n';
import { usePracticeName } from '@frontend/practice-name';
import { useAppScopeStore } from '@frontend/scope';
import { formatDynamicFieldKey } from '@frontend/string';
import { theme } from '@frontend/theme';
import { phone, Stepper, TagType, Text, useDebouncedFn } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { overrideTextStyles, stepValueContainerStyles } from '../styles';
import { Step, StepId } from '../types';

type UseEmailContentStepParams = {
  contentError: string;
  next: (stepId: StepId) => void;
  previous: (stepId: StepId) => void;
};

export const useTextContentStep = ({ contentError, next, previous }: UseEmailContentStepParams): Step => {
  const { t } = useTranslation('bulk-messaging');
  const { messageTemplate, setMessageTemplate } = useBulkMessageEditorContext();

  const id: StepId = 'content';

  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0] ?? '';

  const { data: phoneNumber } = BulkMessagesQueries.useDefaultPhoneRecord({
    locationId,
    numberType: NumberType_Enum.SMS,
  });

  const practiceName = usePracticeName(locationId, 'Awesome Practice');

  const tags: TagType[] = [
    {
      label: t('First Name'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.FIRST_NAME),
      value: 'Matthew',
    },
    {
      label: t('Last Name'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.LAST_NAME),
      value: 'Smith',
    },
    {
      label: t('Preferred Name'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.PREFERRED_NAME),
      value: 'Matt',
    },
    {
      label: t('Practice Name'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_NAME),
      value: practiceName,
    },
    {
      label: t('Practice Phone'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE),
      value: phone(phoneNumber ?? '(555) 555-5555', { hideCountry: true }),
    },
    {
      label: t('Online Bill Pay Link'),
      key: formatDynamicFieldKey(DynamicFieldKey_Enum.ONLINE_BILL_PAY_LINK),
      value: DEFAULT_BULK_SHORT_URL,
    },
  ];

  const debouncedSetTemplateHtml = useDebouncedFn((template: string) => {
    setMessageTemplate(template);
  }, 1000);

  return {
    id,
    Component: (
      <>
        <Stepper.Title>{t('Text Message Content')}</Stepper.Title>

        <Stepper.Content>
          <Text css={{ marginBottom: theme.spacing(2) }}>
            {t('Customize your message below. Use dynamic fields to personalize your message for each recipient.')}
          </Text>
          <BulkTextTemplateEditor
            error={contentError}
            initialTemplate={messageTemplate}
            onBlur={() => {}}
            onChange={debouncedSetTemplateHtml}
            tags={tags}
            isNewTag
          />
        </Stepper.Content>

        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={() => previous(id)} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate onClick={() => next(id)} position='primary' type='none'>
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
      </>
    ),
    collapsedValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Text Message Content')}</Text>
        <Text
          as='div'
          css={{
            ...overrideTextStyles,
            textTransform: 'initial',
            '.bulk-text-template-preview': {
              padding: 0,
              margin: 0,
            },
          }}
        >
          <BulkTextTemplateEditor initialTemplate={messageTemplate} tags={tags} isNewTag previewOnly />
        </Text>
      </div>
    ),
  };
};
